import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Navigate } from "react-router-dom";
import isEqual from "lodash.isequal";
import omit from "lodash.omit";
import Address from "byzantine/src/Address";
import User from "byzantine/src/User";
import { Button, Checkbox, Row } from "@narmi/design_system";
import {
  useFormData,
  useNotificationContext,
  ContextForm,
  useLoadingContext,
} from "cerulean";
import type { InstitutionFeatures } from "byzantine/src/types";
import Organization from "byzantine/src/Organization";
import BankingUSAddressField from "../../../../address/BankingUSAddressField";
import { useCurrentUser } from "../../../../contexts/CurrentUserContext";
import { useUserFeatures } from "../../../../contexts/UserFeaturesContext";
import { useInstitutionSettings } from "../../../../contexts/InstitutionSettingsContext";
import utils from "../../../../../utils";
import { checkAuthSession } from "../../../../../src/utils";
import BusinessAddressEditDialog from "./BusinessAddressEditDialog";
import useSudoApiHandler from "../../../../SudoContext/useSudoApiHandler";
import { useFingerPrintData } from "../../../../fingerprint";

interface AddressEditDialogBodyProps {
  doAddressesMatch: boolean;
  closeDialog: () => void;
}

interface AddressFormData {
  primary_address: Address; // eslint-disable-line camelcase
  mailing_address: Address; // eslint-disable-line camelcase
}

const AddressEditDialogBody = ({
  doAddressesMatch,
  closeDialog,
}: AddressEditDialogBodyProps) => {
  const { l10n } = useLocalization();
  const { currentUser, setCurrentUser } = useCurrentUser();
  const { sendNotificationToParent } = useNotificationContext();
  const { setIsLoading } = useLoadingContext();
  const institutionSettings = useInstitutionSettings();
  const handleSudoRequest = useSudoApiHandler();
  const { formData, setFormData, onChange } = useFormData({
    primary_address: currentUser?.getPrimaryAddress()?.toJSON(),
    mailing_address: currentUser?.getMailingAddress()?.toJSON(),
  });
  const [addressesMatch, setAddressesMatch] = useState(doAddressesMatch);
  const [primaryAddressErrors, setPrimaryAddressErrors] = useState({});
  const [mailingAddressErrors, setMailingAddressErrors] = useState({});
  const { deviceId, deviceRequestId } = useFingerPrintData("update_address");
  const streetAddressLabel = l10n.getString("label-street-address");
  const hasMailingAddressOnFile = !!currentUser?.getMailingAddress();

  const onPrimaryAddressChange = (update: Partial<Address>) => {
    setFormData((prev: AddressFormData) => {
      const updatedPrimary = { ...prev.primary_address, ...update };
      return {
        ...prev,
        primary_address: updatedPrimary,
        ...(addressesMatch
          ? { mailing_address: { ...updatedPrimary, type: "mailing" } }
          : {}),
      };
    });
  };

  const onMailingAddressChange = (update: Partial<Address>) => {
    setFormData((prev: AddressFormData) => ({
      ...prev,
      mailing_address: { ...prev.mailing_address, ...update },
    }));
  };

  const validateAddresses = () => {
    const primaryErrors = utils.validateAddress(formData.primary_address);
    setPrimaryAddressErrors(primaryErrors);

    if (!addressesMatch) {
      const mailingErrors = utils.validateAddress(formData.mailing_address);
      setMailingAddressErrors(mailingErrors);
      if (Object.keys(mailingErrors).length !== 0) {
        return "Please correct the below errors";
      }
    } else {
      setMailingAddressErrors({});
    }

    if (Object.keys(primaryErrors).length !== 0) {
      return "Please correct the below errors";
    }
    return null;
  };

  useEffect(() => {
    if (addressesMatch) {
      setFormData({
        ...formData,
        mailing_address: { ...formData.primary_address, type: "mailing" },
      });
    }
  }, [addressesMatch]);

  const onSubmit = async (callback: CallableFunction) => {
    if (validateAddresses() !== null) {
      setIsLoading(false);
      return;
    }
    try {
      const primaryAddress = new Address(formData.primary_address);
      let mailingAddress = new Address(formData.mailing_address);
      if (addressesMatch) {
        mailingAddress = new Address({
          ...formData.primary_address,
          type: "mailing",
        });
      }
      // CDB-4858: only send the mailing PUT when the institution offers a
      // separate mailing address AND we would not be creating one from scratch.
      // A `type: "mailing"` PUT for a user with no mailing address on file is
      // routed into `CoreAPI().create_user_address()`, which on multi-address
      // cores (DNA and friends) writes a brand new Alternate address record on
      // the core that nothing in the product can remove. When one already
      // exists we still send it, so it stays in sync with the primary address.
      const shouldUpdateMailingAddress =
        !!institutionSettings.has_mailing_address &&
        (!addressesMatch || hasMailingAddressOnFile);

      // we first update the primary address and then the mailing address
      let updatedUser = await handleSudoRequest(() => {
        if (!currentUser) throw new Error("No current user");
        return currentUser.updateAddress(
          primaryAddress,
          deviceId,
          deviceRequestId
        );
      });
      if (shouldUpdateMailingAddress) {
        updatedUser = await handleSudoRequest(() => {
          if (!currentUser) throw new Error("No current user");
          return currentUser.updateAddress(
            mailingAddress,
            deviceId,
            deviceRequestId
          );
        });
      }

      if (updatedUser) {
        const { addresses } = updatedUser;
        setCurrentUser(new User({ ...currentUser, addresses }));
        const updatedAddressesNotificationText = l10n.getString(
          "address-edit-dialog-success",
          { mailingAndPrimary: `${shouldUpdateMailingAddress}` }
        );
        sendNotificationToParent({
          type: "success",
          text: updatedAddressesNotificationText,
        });
        callback();
      }
      closeDialog();
    } catch (error) {
      if (error instanceof Error) {
        callback(error.message);
      } else {
        callback(error);
      }
      // Check if user was blocked and locked
      await checkAuthSession(
        l10n.getString("logout-message-blocked-and-locked")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="margin--top--s" />
      <div
        className="fontWeight--bold  margin--bottom--s"
        style={{ color: "var(--color-black)" }}
      >
        {l10n.getString("label-personal-primary-address")}
      </div>
      <ContextForm data={formData} onChange={onChange}>
        <ContextForm.Field field="primary_address">
          <BankingUSAddressField
            addressType={"primary"}
            onUpdate={onPrimaryAddressChange}
            streetAddressLabel={streetAddressLabel}
            data={formData.primary_address || {}}
            showTitle={false}
            errors={primaryAddressErrors}
            marginTop="none"
          />
        </ContextForm.Field>
        {institutionSettings.has_mailing_address && (
          <>
            <div
              className="fontWeight--bold margin--bottom--s"
              style={{ color: "var(--color-black)" }}
            >
              {l10n.getString("label-personal-mailing-address")}
            </div>
            {!addressesMatch && (
              <ContextForm.Field field="mailing_address">
                <BankingUSAddressField
                  addressType={"mailing"}
                  onUpdate={onMailingAddressChange}
                  streetAddressLabel={streetAddressLabel}
                  data={formData.mailing_address || {}}
                  showTitle={false}
                  errors={mailingAddressErrors}
                  marginTop="none"
                  marginBottom="margin--bottom--s"
                />
              </ContextForm.Field>
            )}
            <Checkbox
              onChange={() => {
                if (!addressesMatch) setMailingAddressErrors({});
                setAddressesMatch(!addressesMatch);
              }}
              label={l10n.getString("label-same-residential-address")}
              checked={addressesMatch}
            />
          </>
        )}
        <div className="margin--bottom--xl" />
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              type="button"
              onClick={closeDialog}
              kind="negative"
              label={l10n.getString("button-cancel")}
            />
          </Row.Item>
          <Row.Item shrink>
            <ContextForm.Action onSubmit={onSubmit}>
              <Button
                kind="primary"
                label={l10n.getString("button-save-changes")}
              />
            </ContextForm.Action>
          </Row.Item>
        </Row>
      </ContextForm>
    </>
  );
};

interface AddressEditDialogBodyWrapperProps {
  closeDialog: () => void;
  organization?: Organization;
  setOrganization: (organization: Organization) => void;
}

const AddressEditDialogBodyWrapper = ({
  closeDialog,
  organization,
  setOrganization,
}: AddressEditDialogBodyWrapperProps) => {
  const { currentUser } = useCurrentUser();
  const primaryAddress = currentUser?.getPrimaryAddress();
  const mailingAddress = currentUser?.getMailingAddress();

  const doAddressesMatch =
    isEqual(omit(primaryAddress, ["type"]), omit(mailingAddress, ["type"])) ||
    !mailingAddress;

  const institutionSettings = useInstitutionSettings();
  const userFeatures = useUserFeatures() as InstitutionFeatures;

  if (
    !institutionSettings?.core_update_address ||
    !userFeatures.address_change ||
    !currentUser?.isPersonalUserOrBusinessAdmin()
  ) {
    return <Navigate to={"/profile"} replace />;
  }

  if (currentUser.isBusiness()) {
    return (
      <BusinessAddressEditDialog
        closeDialog={closeDialog}
        organization={organization}
        setOrganization={setOrganization}
      />
    );
  }

  return (
    <AddressEditDialogBody
      doAddressesMatch={doAddressesMatch}
      closeDialog={closeDialog}
    />
  );
};

export default AddressEditDialogBodyWrapper;
